import React, { useState, useEffect } from 'react';
import { 
  Heart, 
  Brain, 
  Clock, 
  Pause, 
  Play, 
  RotateCcw, 
  ChevronLeft, 
  ChevronRight, 
  Trophy, 
  Stethoscope,
  Activity,
  Award,
  Star,
  Copy,
  CheckCircle,
  Eye,
  X,
  ShieldCheck
} from 'lucide-react';

const QUIZ_DATA = [
  {
    id: 1,
    question: "Which organ is primarily responsible for detoxification in the human body?",
    options: ["Kidney", "Lungs", "Liver", "Pancreas"],
    answer: 2,
    fact: "The liver performs over 500 vital functions, including filtering toxins from the blood!"
  },
  {
    id: 2,
    question: "What is the normal resting heart rate for a healthy adult?",
    options: ["40–60 beats per minute", "60–100 beats per minute", "100–140 beats per minute", "140–180 beats per minute"],
    answer: 1,
    fact: "Athletes often have lower resting heart rates, sometimes as low as 40 BPM."
  },
  {
    id: 3,
    question: "Which vitamin deficiency causes scurvy?",
    options: ["Vitamin A", "Vitamin B12", "Vitamin D", "Vitamin C"],
    answer: 3,
    fact: "Sailors used to carry citrus fruits to prevent scurvy during long voyages."
  },
  {
    id: 4,
    question: "Which blood cells are responsible for fighting infections?",
    options: ["Red blood cells", "Platelets", "White blood cells", "Plasma cells"],
    answer: 2,
    fact: "There are several types of white blood cells, including neutrophils, lymphocytes, and monocytes."
  },
  {
    id: 5,
    question: "What is the normal pH range of human blood?",
    options: ["6.8–7.0", "7.0–7.2", "7.35–7.45", "7.6–7.8"],
    answer: 2,
    fact: "Human blood is slightly basic. Even a small shift in pH can be life-threatening."
  }
];

const TIMER_DURATION = 20;

export default function App() {
  const [currentStep, setCurrentStep] = useState('start'); 
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState(new Array(QUIZ_DATA.length).fill(null));
  const [timeLeft, setTimeLeft] = useState(TIMER_DURATION);
  const [isPaused, setIsPaused] = useState(false);
  const [score, setScore] = useState(0);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [showCorrection, setShowCorrection] = useState(false);
  const [copied, setCopied] = useState(false);

  // Main Quiz Timer
  useEffect(() => {
    let timer;
    if (currentStep === 'quiz' && !isPaused && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && currentStep === 'quiz') {
      handleNext();
    }
    return () => clearInterval(timer);
  }, [currentStep, isPaused, timeLeft]);

  // Reward Modal Auto-close Timer (5 seconds)
  useEffect(() => {
    let timer;
    if (showRewardModal) {
      timer = setTimeout(() => {
        setShowRewardModal(false);
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [showRewardModal]);

  const startQuiz = () => {
    setCurrentStep('quiz');
    setCurrentIndex(0);
    setSelectedAnswers(new Array(QUIZ_DATA.length).fill(null));
    setTimeLeft(TIMER_DURATION);
    setIsPaused(false);
    setShowCorrection(false);
    setScore(0);
  };

  const handleSelect = (optionIndex) => {
    if (isPaused) return;
    const newAnswers = [...selectedAnswers];
    newAnswers[currentIndex] = optionIndex;
    setSelectedAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentIndex < QUIZ_DATA.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setTimeLeft(TIMER_DURATION);
    } else {
      calculateResult();
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setTimeLeft(TIMER_DURATION);
    }
  };

  const calculateResult = () => {
    let finalScore = 0;
    selectedAnswers.forEach((answer, idx) => {
      if (answer === QUIZ_DATA[idx].answer) {
        finalScore++;
      }
    });
    setScore(finalScore);
    setCurrentStep('result');
    if (finalScore >= 2) {
      setShowRewardModal(true);
    }
  };

  const copyToClipboard = (text) => {
    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getRewardInfo = () => {
    if (score === 5) return { 
      discount: "20%", 
      code: "SURGEON20", 
      msg: "Exceptional performance! You have mastered medical fundamentals.",
      color: "from-yellow-400 to-orange-500"
    };
    if (score >= 3) return { 
      discount: "15%", 
      code: "HEAL15", 
      msg: "Outstanding! You're well on your way to becoming a medical expert.",
      color: "from-blue-400 to-indigo-500"
    };
    if (score === 2) return { 
      discount: "10%", 
      code: "MED10", 
      msg: "Great effort! A solid foundation of medical knowledge.",
      color: "from-emerald-400 to-teal-500"
    };
    return null;
  };

  const getRank = () => {
    if (score === 5) return { title: "Grand Surgeon", sub: "Master of Human Biology", icon: <Trophy className="w-16 h-16 text-yellow-400" />, color: "text-yellow-400" };
    if (score >= 3) return { title: "Medical Specialist", sub: "Impressive Medical Knowledge", icon: <Award className="w-16 h-16 text-blue-400" />, color: "text-blue-400" };
    if (score === 2) return { title: "Med Student", sub: "Good foundation! Keep learning.", icon: <Stethoscope className="w-16 h-16 text-green-400" />, color: "text-green-400" };
    return { title: "Aspiring Doc", sub: "Time to hit the medical books!", icon: <Brain className="w-16 h-16 text-gray-400" />, color: "text-gray-400" };
  };

  const RewardModal = () => {
    const reward = getRewardInfo();
    if (!reward || !showRewardModal) return null;

    return (
      <div 
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-md animate-in fade-in duration-300"
        onClick={() => setShowRewardModal(false)}
      >
        <div 
          className="bg-slate-900 w-full max-w-md border border-white/10 rounded-[2rem] overflow-hidden shadow-2xl animate-in zoom-in duration-300 relative"
          onClick={(e) => e.stopPropagation()}
        >
          <div className={`h-32 bg-gradient-to-br ${reward.color} flex items-center justify-center relative`}>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                setShowRewardModal(false);
              }}
              className="absolute top-4 right-4 p-2 bg-black/20 hover:bg-black/40 rounded-full text-white transition-colors z-10"
              aria-label="Close Modal"
            >
              <X size={20} />
            </button>
            <ShieldCheck className="w-16 h-16 text-white drop-shadow-lg" />
          </div>
          <div className="p-8 text-center">
            <h3 className="text-2xl font-black text-white mb-2">Exclusive Reward Unlocked!</h3>
            <p className="text-slate-300 mb-6">{reward.msg}</p>
            
            <div className="bg-white/5 border border-dashed border-white/20 rounded-2xl p-6 mb-6">
              <span className="text-sm uppercase tracking-widest text-slate-400 block mb-1">Your Discount</span>
              <div className="text-4xl font-black text-white mb-4">{reward.discount} OFF</div>
              
              <div className="flex items-center gap-2 bg-black/40 rounded-xl p-3 border border-white/10">
                <code className="flex-1 font-mono text-xl text-blue-400 font-bold tracking-wider">{reward.code}</code>
                <button 
                  onClick={() => copyToClipboard(reward.code)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white flex items-center gap-2"
                >
                  {copied ? <CheckCircle size={18} className="text-green-400" /> : <Copy size={18} />}
                  <span className="text-xs font-bold">{copied ? "Copied!" : "Copy"}</span>
                </button>
              </div>
            </div>
            
            <p className="text-xs text-slate-500 mb-6 italic">*Valid for your first clinical visit as a new patient.</p>
            
            <button 
              onClick={() => setShowRewardModal(false)}
              className="w-full py-4 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-blue-900/20"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderStart = () => (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-6 animate-in fade-in zoom-in duration-500">
      <div className="relative mb-8">
        <div className="absolute inset-0 bg-blue-500 blur-3xl opacity-20 animate-pulse"></div>
        <Heart className="w-24 h-24 text-red-500 animate-bounce relative z-10" />
      </div>
      <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">
        MedQuiz <span className="text-blue-400">Challenge</span>
      </h1>
      <p className="text-blue-100 text-lg mb-8 max-w-md">
        Test your knowledge of the human body. 10 Questions, 20 seconds each. Are you ready?
      </p>
      <button 
        onClick={startQuiz}
        className="px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-full font-bold text-xl shadow-lg transform transition hover:scale-105 active:scale-95 flex items-center gap-2"
      >
        <Play className="fill-current" /> Start Operation
      </button>
    </div>
  );

  const renderQuiz = () => {
    const question = QUIZ_DATA[currentIndex];
    const progress = ((currentIndex + 1) / QUIZ_DATA.length) * 100;
    const timerColor = timeLeft < 5 ? 'text-red-400' : 'text-blue-300';

    return (
      <div className="w-full max-w-2xl mx-auto p-4 md:p-6 animate-in slide-in-from-bottom-4 duration-500">
        <div className="flex items-center justify-between mb-6 bg-white/10 backdrop-blur-md p-4 rounded-2xl border border-white/20">
          <div className="flex items-center gap-2">
            <Activity className="text-red-400 w-5 h-5 animate-pulse" />
            <span className="text-white font-semibold">Q{currentIndex + 1} of {QUIZ_DATA.length}</span>
          </div>
          
          <div className="flex items-center gap-4">
             <div className={`flex items-center gap-2 font-mono text-2xl font-bold ${timerColor} transition-colors duration-300`}>
              <Clock className="w-5 h-5" />
              {timeLeft}s
            </div>
            <button 
              onClick={() => setIsPaused(!isPaused)}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all"
            >
              {isPaused ? <Play size={20} /> : <Pause size={20} />}
            </button>
          </div>
        </div>

        <div className="w-full h-2 bg-white/10 rounded-full mb-8 overflow-hidden">
          <div 
            className="h-full bg-blue-500 transition-all duration-500 ease-out shadow-[0_0_10px_rgba(59,130,246,0.5)]" 
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className={`relative bg-white/10 backdrop-blur-xl p-8 rounded-3xl border border-white/20 shadow-2xl transition-all duration-300 ${isPaused ? 'opacity-50 blur-sm pointer-events-none' : ''}`}>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8 leading-tight">
            {question.question}
          </h2>

          <div className="grid grid-cols-1 gap-4">
            {question.options.map((option, idx) => (
              <button
                key={idx}
                onClick={() => handleSelect(idx)}
                className={`group relative p-4 text-left rounded-xl border-2 transition-all duration-200 transform hover:-translate-y-1 active:translate-y-0
                  ${selectedAnswers[currentIndex] === idx 
                    ? 'bg-blue-600 border-blue-400 text-white shadow-[0_0_20px_rgba(37,99,235,0.4)]' 
                    : 'bg-white/5 border-white/10 text-blue-100 hover:border-blue-400/50 hover:bg-white/10'
                  }`}
              >
                <div className="flex items-center gap-4">
                  <span className={`flex-shrink-0 w-8 h-8 rounded-full border flex items-center justify-center font-bold
                    ${selectedAnswers[currentIndex] === idx ? 'bg-white text-blue-600' : 'border-white/20'}`}>
                    {String.fromCharCode(65 + idx)}
                  </span>
                  <span className="text-lg font-medium">{option}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between mt-8">
          <button 
            disabled={currentIndex === 0}
            onClick={handleBack}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-all
              ${currentIndex === 0 ? 'opacity-30 cursor-not-allowed text-white' : 'bg-white/10 hover:bg-white/20 text-white'}`}
          >
            <ChevronLeft size={20} /> Back
          </button>
          
          <button 
            onClick={handleNext}
            className="flex items-center gap-2 px-8 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-full font-bold shadow-lg transform transition active:scale-95"
          >
            {currentIndex === QUIZ_DATA.length - 1 ? 'Finish' : 'Next'} <ChevronRight size={20} />
          </button>
        </div>
      </div>
    );
  };

  const renderResult = () => {
    const rank = getRank();
    const reward = getRewardInfo();

    return (
      <div className="w-full max-w-2xl mx-auto p-6 animate-in zoom-in duration-700">
        <div className="bg-white/10 backdrop-blur-2xl rounded-[2.5rem] p-10 border border-white/20 shadow-2xl relative overflow-hidden text-center">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-blue-500/20 blur-[80px] rounded-full"></div>
          
          <div className="mb-6 flex flex-col items-center">
            <div className="p-6 bg-white/5 rounded-full mb-4 inline-block animate-bounce-slow">
              {rank.icon}
            </div>
            <h2 className={`text-3xl font-black mb-2 ${rank.color}`}>{rank.title}</h2>
            <p className="text-blue-100 font-medium mb-1">{rank.sub}</p>
            <p className="text-slate-400 text-sm italic">Thank you for participating in our Medical Knowledge Assessment!</p>
          </div>

          <div className="flex justify-center gap-4 mb-8">
            <div className="bg-white/5 px-6 py-4 rounded-2xl border border-white/10 flex-1">
              <span className="block text-3xl font-bold text-white">{score}</span>
              <span className="text-xs uppercase tracking-widest text-blue-300 font-bold">Score</span>
            </div>
            <div className="bg-white/5 px-6 py-4 rounded-2xl border border-white/10 flex-1">
              <span className="block text-3xl font-bold text-white">{Math.round((score / QUIZ_DATA.length) * 100)}%</span>
              <span className="text-xs uppercase tracking-widest text-blue-300 font-bold">Accuracy</span>
            </div>
          </div>

          {showCorrection ? (
            <div className="space-y-4 mb-10 text-left max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
              <h3 className="text-xl font-bold text-white border-b border-white/10 pb-2 mb-4">Correct Answers</h3>
              {QUIZ_DATA.map((item, idx) => {
                const userAns = selectedAnswers[idx];
                const isCorrect = userAns === item.answer;
                return (
                  <div key={item.id} className="bg-white/5 rounded-xl p-4 border border-white/10">
                    <div className="flex gap-3 items-start mb-2">
                      <span className="bg-white/10 px-2 py-1 rounded text-xs font-bold text-white shrink-0">Q{idx + 1}</span>
                      <p className="text-white font-medium text-sm leading-relaxed">{item.question}</p>
                    </div>
                    <div className="space-y-1 pl-11">
                      <div className={`text-sm flex items-center gap-2 ${isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                        <span className="font-bold">Your answer:</span> 
                        {userAns !== null ? item.options[userAns] : 'Skipped'}
                      </div>
                      {!isCorrect && (
                        <div className="text-sm text-blue-300 flex items-center gap-2">
                          <span className="font-bold">Correct answer:</span> {item.options[item.answer]}
                        </div>
                      )}
                      <p className="text-slate-400 text-[11px] leading-snug mt-2 italic bg-black/20 p-2 rounded">
                        <Brain size={10} className="inline mr-1" /> {item.fact}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-6 mb-10">
               {reward && (
                <button 
                  onClick={() => setShowRewardModal(true)}
                  className="w-full flex items-center justify-between bg-gradient-to-r from-blue-600/20 to-indigo-600/20 hover:from-blue-600/30 hover:to-indigo-600/30 border border-white/10 p-4 rounded-2xl group transition-all"
                >
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="text-blue-400" />
                    <div className="text-left">
                      <div className="text-white font-bold">View My Reward</div>
                      <div className="text-blue-300 text-xs">Tier: {reward.discount} Discount unlocked</div>
                    </div>
                  </div>
                  <ChevronRight className="text-white opacity-40 group-hover:opacity-100 transition-opacity" />
                </button>
              )}

              <div className="grid grid-cols-2 gap-4">
                 <button 
                  onClick={() => setShowCorrection(true)}
                  className="flex flex-col items-center justify-center p-6 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl transition-all group"
                >
                  <Eye className="text-blue-400 mb-2 group-hover:scale-110 transition-transform" />
                  <span className="text-white font-bold text-sm">Review Answers</span>
                </button>

                <button 
                  onClick={startQuiz}
                  className="flex flex-col items-center justify-center p-6 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl transition-all group"
                >
                  <RotateCcw className="text-green-400 mb-2 group-hover:rotate-45 transition-transform" />
                  <span className="text-white font-bold text-sm">Try Again</span>
                </button>
              </div>
            </div>
          )}

          {showCorrection && (
             <button 
              onClick={() => setShowCorrection(false)}
              className="w-full py-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-2xl font-bold transition-all mb-4"
            >
              Back to Summary
            </button>
          )}

          <button 
            onClick={() => setCurrentStep('start')}
            className="w-full py-4 bg-transparent text-slate-500 font-bold hover:text-white transition-colors"
          >
            Return to Main Menu
          </button>
        </div>
        
        {score >= 3 && (
          <div className="absolute inset-0 pointer-events-none">
             {[...Array(12)].map((_, i) => (
               <Star 
                key={i} 
                className={`absolute text-yellow-400 opacity-20 animate-pulse`} 
                style={{
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  transform: `scale(${Math.random() * 1.5})`,
                  animationDelay: `${Math.random() * 2}s`
                }}
               />
             ))}
          </div>
        )}
        <RewardModal />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 font-sans selection:bg-blue-500/30 overflow-x-hidden flex items-center justify-center py-10">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-blue-600/20 blur-[120px] rounded-full animate-pulse"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-indigo-600/20 blur-[120px] rounded-full animate-pulse delay-700"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full opacity-[0.03]" 
             style={{ backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
      </div>

      <main className="relative z-10 w-full px-4">
        {currentStep === 'start' && renderStart()}
        {currentStep === 'quiz' && renderQuiz()}
        {currentStep === 'result' && renderResult()}
      </main>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(-5%); }
          50% { transform: translateY(0); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 3s infinite ease-in-out;
        }
        .animate-in {
          animation: enter 0.5s ease-out;
        }
        @keyframes enter {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.3);
        }
      `}} />
    </div>
  );
}